/**
 * Fire-and-forget pushEvent for LiveView hooks.
 *
 * Hook replies to the server (viewport size, extension-bridge results, picker
 * close, …) are fire-and-forget: nothing on the client awaits them. When the
 * view's channel has not joined yet — or has dropped, or is navigating away —
 * `hook.pushEvent()` *rejects the promise it returns*; it does not throw
 * (`view.js pushHookEvent` does a bare `return Promise.reject(...)`). With
 * nothing attached to that promise the rejection surfaces as an unhandled
 * rejection, and a Sentry error, for something entirely benign: the user simply
 * left.
 *
 * The rejection — not a synchronous throw — is therefore what has to be
 * swallowed, and the `.catch()` below is what actually makes this safe. The
 * `liveSocket.isConnected()` pre-check is only a cheap fast path and is NOT
 * sufficient on its own: it tests the *websocket transport*, whereas LiveView
 * rejects on the *view's own channel* (`view.channel.canPush()`). Brainbox drops
 * and reconnects the socket during auth, so there is a window where the
 * transport is up (pre-check passes) while this view's channel has yet to
 * join (push still rejects).
 *
 * Only use this for pushes whose loss is harmless; anything the client needs a
 * reply to should use pushEvent directly and handle its own failure.
 */
export function safePushEvent(hook, event, payload) {
  try {
    if (hook.isMounted === false) return;
    if (!hook.liveSocket || !hook.liveSocket.isConnected()) return;
    const pushed = hook.pushEvent(event, payload);
    // The view was not joined, or disconnected under us: there is nothing live
    // to receive this reply, so dropping it is the correct outcome.
    if (pushed && typeof pushed.catch === 'function') pushed.catch(() => {});
  } catch {
    // The hook is no longer attached to a view — `__view()` throws once the
    // hook has been destroyed. Same benign outcome as a rejected push.
  }
}
